import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { deleteStoredFile, objectFileName, objectPathFromUrl, uploadFile } from '../../../api/files';
import './AppointmentMedia.css';

const DocIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
);
const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
);
const DeleteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
);
const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
);
const PdfIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 9.5c0 .83-.67 1.5-1.5 1.5H11v1.5h1v.5H9.5v-3.5H10c.83 0 1.5.67 1.5 1.5v.5zm4.5.5c-.28 0-.5-.22-.5-.5v-3c0-.28.22-.5.5-.5h1.5c.83 0 1.5.67 1.5 1.5v.5c0 .83-.67 1.5-1.5 1.5h-1v1.5h.5zm.5-2h.5v-1h-.5v1z"/></svg>
);
const FileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
);
const TextIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M2.5 4v3h5v12h3V7h5V4h-13zm19 5h-9v3h3v7h3v-7h3V9z"/></svg>
);

const getDocumentIcon = (fileType) => {
  if (fileType === 'application/pdf') return <PdfIcon />;
  if (fileType === 'text/plain') return <TextIcon />;
  if (fileType && fileType.includes('document')) return <FileIcon />;
  return <DocIcon />;
};

const AppointmentMedia = ({ appointmentId, documents = [], photos = [], onUpdate }) => {
  const { companyData } = useAuth();
  const { showToast } = useNotification();
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [localPhotos, setLocalPhotos] = useState(photos);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, file: null, type: null });

  useEffect(() => {
    setLocalDocuments(documents);
    setLocalPhotos(photos);
  }, [documents, photos]);

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (type === 'photo' && !file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('File size should not exceed 5MB', 'error');
      return;
    }

    setUploading(true);

    if (!companyData?.id) {
      showToast('Missing company', 'error');
      return;
    }

    try {
      const uniqueFilename = objectFileName(file);
      const basePath = `companies/${companyData.id}/appointments/${appointmentId}`;
      const objectPath = `${basePath}/${type}s/${uniqueFilename}`;
      const stored = await uploadFile(file, objectPath);
      const downloadUrl = stored.url;

      const newFile = {
        name: uniqueFilename,
        originalName: file.name,
        url: downloadUrl,
        storagePath: objectPath,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        size: file.size
      };

      if (type === 'document') {
        setLocalDocuments(prev => [...prev, newFile]);
      } else {
        setLocalPhotos(prev => [...prev, newFile]);
      }

      if (onUpdate) {
        await onUpdate(type, newFile);
        showToast(`${type === 'photo' ? 'Photo' : 'Document'} uploaded successfully`, 'success');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      showToast('Failed to upload file. Please try again.', 'error');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const deleteFile = async (file, type) => {
    try {
      if (!companyData?.id) {
        showToast('Missing company', 'error');
        return;
      }
      const basePath = `companies/${companyData.id}/appointments/${appointmentId}`;
      const objectPath = file.storagePath || objectPathFromUrl(file.url) || `${basePath}/${type}s/${file.name}`;
      await deleteStoredFile(objectPath);

      if (type === 'document') {
        setLocalDocuments(prev => prev.filter(f => f.name !== file.name));
      } else {
        setLocalPhotos(prev => prev.filter(f => f.name !== file.name));
      }

      if (onUpdate) {
        await onUpdate(type, file, 'delete');
        showToast(`${type === 'photo' ? 'Photo' : 'Document'} deleted successfully`, 'success');
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      showToast('Failed to delete file. Please try again.', 'error');
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete.file || !confirmDelete.type) return;
    try {
      await deleteFile(confirmDelete.file, confirmDelete.type);
      setConfirmDelete({ open: false, file: null, type: null });
    } catch {
      setConfirmDelete({ open: false, file: null, type: null });
    }
  };

  const handlePreview = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleDocumentPreview = (file) => {
    setDocumentPreviewUrl(file.url);
    setDocumentType(file.type);
    setDocumentPreviewOpen(true);
  };

  return (
    <div className="am">
      <div className="am__tabs">
        <button
          type="button"
          className={`am__tab ${activeTab === 0 ? 'am__tab--active' : ''}`}
          onClick={() => setActiveTab(0)}
          aria-selected={activeTab === 0}
        >
          <DocIcon />
          <span>Documents</span>
        </button>
        <button
          type="button"
          className={`am__tab ${activeTab === 1 ? 'am__tab--active' : ''}`}
          onClick={() => setActiveTab(1)}
          aria-selected={activeTab === 1}
        >
          <ImageIcon />
          <span>Photos</span>
        </button>
      </div>

      {activeTab === 0 && (
        <div className="am__panel" role="tabpanel">
          <div className="am__content">
            {localDocuments.length === 0 ? (
              <p className="am__empty">No documents uploaded yet</p>
            ) : (
              <div className="am__grid">
                {localDocuments.map((file, index) => (
                  <div
                    key={index}
                    className="am__card am__card--doc"
                    onClick={() => handleDocumentPreview(file)}
                  >
                    <div className="am__card-preview am__card-preview--doc">
                      {getDocumentIcon(file.type)}
                      <span className="am__card-filename">{file.originalName || file.name}</span>
                    </div>
                    <div className="am__card-actions">
                      <a href={file.url} target="_blank" rel="noopener noreferrer" download={file.originalName || file.name} className="am__card-btn" onClick={(e) => e.stopPropagation()} aria-label="Download">
                        <DownloadIcon />
                      </a>
                      <button type="button" className="am__card-btn am__card-btn--danger" onClick={(e) => { e.stopPropagation(); setConfirmDelete({ open: true, file, type: 'document' }); }} aria-label="Delete">
                        <DeleteIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <hr className="am__divider" />
          <div className="am__upload-wrap">
            <label className="am__upload-btn" style={{ opacity: uploading ? 0.7 : 1 }}>
              <span>Upload Document</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileUpload(e, 'document')}
                disabled={uploading}
                className="am__upload-input"
              />
            </label>
          </div>
        </div>
      )}

      {activeTab === 1 && (
        <div className="am__panel" role="tabpanel">
          <div className="am__content">
            {localPhotos.length === 0 ? (
              <p className="am__empty">No photos uploaded yet</p>
            ) : (
              <div className="am__grid">
                {localPhotos.map((file, index) => (
                  <div key={index} className="am__card">
                    <button type="button" className="am__card-preview am__card-preview--img" onClick={() => handlePreview(file.url)}>
                      <img src={file.url} alt={file.originalName || file.name} />
                    </button>
                    <div className="am__card-actions">
                      <a href={file.url} target="_blank" rel="noopener noreferrer" download={file.originalName || file.name} className="am__card-btn" aria-label="Download">
                        <DownloadIcon />
                      </a>
                      <button type="button" className="am__card-btn am__card-btn--danger" onClick={() => setConfirmDelete({ open: true, file, type: 'photo' })} aria-label="Delete">
                        <DeleteIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <hr className="am__divider" />
          <div className="am__upload-wrap">
            <label className="am__upload-btn" style={{ opacity: uploading ? 0.7 : 1 }}>
              <span>Upload Photo</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'photo')}
                disabled={uploading}
                className="am__upload-input"
              />
            </label>
          </div>
        </div>
      )}

      {uploading && (
        <div className="am__loading">
          <span className="am__spinner" aria-hidden="true" />
        </div>
      )}

      {previewOpen && previewUrl && (
        <div className="am__overlay" onClick={() => setPreviewOpen(false)} role="dialog" aria-modal="true">
          <div className="am__modal am__modal--img" onClick={(e) => e.stopPropagation()}>
            <img src={previewUrl} alt="Preview" />
            <div className="am__modal-actions">
              <button type="button" className="am__modal-btn" onClick={() => setPreviewOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {documentPreviewOpen && documentPreviewUrl && (
        <div className="am__overlay" onClick={() => setDocumentPreviewOpen(false)} role="dialog" aria-modal="true">
          <div className="am__modal am__modal--doc" onClick={(e) => e.stopPropagation()}>
            {documentType === 'application/pdf' ? (
              <iframe src={`${documentPreviewUrl}#toolbar=0`} title="PDF Preview" className="am__iframe" />
            ) : documentType === 'text/plain' ? (
              <div className="am__doc-wrap">
                <iframe src={documentPreviewUrl} title="Text Preview" className="am__iframe" />
              </div>
            ) : (
              <div className="am__doc-unavailable">
                <FileIcon />
                <p className="am__doc-unavailable-title">Preview not available</p>
                <p className="am__doc-unavailable-text">This file type cannot be previewed directly in the browser.</p>
                <a href={documentPreviewUrl} target="_blank" rel="noopener noreferrer" download className="am__doc-download">
                  <DownloadIcon />
                  Download File
                </a>
              </div>
            )}
            <div className="am__modal-actions">
              <button type="button" className="am__modal-btn" onClick={() => setDocumentPreviewOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete.open && (
        <div className="am__overlay" onClick={() => setConfirmDelete({ open: false, file: null, type: null })} role="dialog" aria-modal="true">
          <div className="am__modal am__modal--confirm" onClick={(e) => e.stopPropagation()}>
            <h3 className="am__confirm-title">Confirm Delete</h3>
            <p className="am__confirm-message">Are you sure you want to delete this {confirmDelete.type}?</p>
            <div className="am__modal-actions">
              <button type="button" className="am__modal-btn am__modal-btn--secondary" onClick={() => setConfirmDelete({ open: false, file: null, type: null })}>Cancel</button>
              <button type="button" className="am__modal-btn am__modal-btn--danger" onClick={handleConfirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentMedia;
